import { useEffect, useState } from 'react';
import { Activity, CalendarHeart, Users, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2G PR 2 — Sports Identity card.
 *
 * Identity drives retention. This card composes four lines that read like
 * a player's living sports identity:
 *   1. Football Regular
 *   2. Saturday 7AM Crew
 *   3. Trusted by 12 players
 *   4. 92% attendance
 *
 * Hidden until the player has any games. Visually distinct from the
 * reputation card — feels like a tribal name tag, not a profile stat block.
 */
const ICON_MAP = { sport: Activity, ritual: CalendarHeart, trust: Users, attendance: ShieldCheck };
const ACCENT_MAP = {
  sport: '#06b6d4', ritual: '#f59e0b', trust: '#ec4899', attendance: '#10b981',
};

export default function ArenaxSportsIdentity() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/arenax/player/sports-identity').then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data || data.is_empty) return null;

  return (
    <div className="rounded-2xl border border-amber-500/15 p-5"
      data-testid="arenax-sports-identity"
      style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.07), var(--ax-card-bg-soft))' }}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] tracking-[0.3em] text-amber-300 font-bold uppercase">YOUR SPORTS IDENTITY</span>
        <span className="text-[10px] text-slate-500 tabular-nums">{data.games_played} games</span>
      </div>
      <div className="space-y-2.5">
        {data.lines.map((line, i) => {
          const Icon = ICON_MAP[line.icon] || Activity;
          const color = ACCENT_MAP[line.icon] || '#06b6d4';
          return (
            <div key={i} className="flex items-center gap-3" data-testid={`arenax-identity-${line.icon}`}>
              <div className="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
                style={{ background: color + '20', border: `1px solid ${color}40` }}>
                <Icon className="h-3.5 w-3.5" style={{ color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-slate-100 leading-tight">{line.label}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{line.hint}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
